import './playerInfo.scss';
import React, {useContext} from 'react';
import {Card, Checkbox, Descriptions, Tag, Select, DatePicker, Space, Typography, Row, Col, Alert, Button, Modal} from 'antd';
import {
    UserOutlined,
    InfoCircleOutlined,
    IdcardOutlined,
    SettingOutlined,
    ClockCircleOutlined,
    SaveOutlined
} from '@ant-design/icons';
import {convertToUTC9} from "../../shared/utils/basic";
import {ENV, USER_STATE, userStateList} from "../../shared/utils/enum";
import dayjs from "dayjs";
import {DarkModeContext} from "../../shared/contexts/darkModeContext";
import {AuthContext} from "../../shared/contexts/authContext";
import {internalApi} from "../../common/axios";
import toast from "react-hot-toast";
import PlayerBadgeGrant from "./PlayerBadgeGrant";

const {Text, Title} = Typography;
const {Option} = Select;

const PlayerInfo = ({playerData, setPlayerData, setIsChange, defaultState, isCopy}) => {
    const {darkMode} = useContext(DarkModeContext);
    const {currentUser} = useContext(AuthContext)

    const handleSetPlayerState = (name, value) => {
        setPlayerData(prevState => ({
            ...prevState,
            [name]: value
        }));

        if (name === 'userState') {
            setIsChange(!(value === defaultState));
            handleSetPlayerState('stateDate', playerData?.stateDate > 0 ? playerData.stateDate : dayjs('2099-12-31').endOf('day').unix());
            handleSetPlayerState('isDelete', false);
        }
    };

    const handleSetPaidLogs = async() => {
        const res = await internalApi.put('/player/setPaid', {
            userKey: playerData.userKey,
            pid: playerData.pid,
            name: playerData.playerInfo?.userInfo?.Nickname,
        });

        if (res.status === 200) {
            toast.success(res.data.message);
        } else {
            toast.error('유료 결제 로그 추가에 실패했습니다');
        }
    }

    const isWithdraw = playerData.userState === USER_STATE.WITHDRAW && playerData.stateReason > 0;

    const getStateTag = (state) => {
        switch (state) {
            case USER_STATE.WITHDRAW:
                return <Tag color="error">탈퇴</Tag>;
            case USER_STATE.SUSPEND:
                return <Tag color="warning">정지</Tag>;
            default:
                return <Tag color="success">정상</Tag>;
        }
    };

    const cardTitle = (
        <Space align="center" size="middle">
            <div className="title-icon">
                <UserOutlined/>
            </div>
            <div>
                <Title level={5} style={{margin: 0, color: 'var(--text-primary)'}}>
                    Player Information
                </Title>
                <Text type="secondary" style={{fontSize: '12px'}}>
                    플레이어 상세 정보
                </Text>
            </div>
        </Space>
    );

    // 플레이어 데이터가 없는 경우
    if (!playerData || !playerData.userKey) {
        return (
            <Card
                title={cardTitle}
                className={`player-info-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
                style={{
                    borderRadius: '8px',
                    border: `1px solid var(--border-color)`,
                    background: `var(--card-background)`,
                    boxShadow: 'var(--card-shadow)'
                }}
            >
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: 'var(--header-background)',
                    borderRadius: '8px',
                    border: '1px dashed var(--border-color)'
                }}>
                    <UserOutlined style={{fontSize: '48px', color: '#ccc', marginBottom: 16}}/>
                    <div>
                        <Text type="secondary" style={{fontSize: '16px'}}>
                            플레이어 데이터가 없습니다
                        </Text>
                        <br/>
                        <Text type="secondary" style={{fontSize: '12px'}}>
                            검색을 통해 플레이어 정보를 조회하세요
                        </Text>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card
            title={cardTitle}
            className={`player-info-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            style={{
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                background: `var(--card-background)`,
                boxShadow: 'var(--card-shadow)'
            }}
        >
            <Row gutter={[16, 24]}>
                {/* 왼쪽 영역 */}
                <Col xs={24} lg={12}>
                    <Row gutter={[0, 24]}>
                        {/* 기본 정보 */}
                        <Col span={24}>
                            <div className="info-section">
                                <div className="section-header">
                                    <IdcardOutlined className="section-icon"/>
                                    <Text strong className="section-title">기본 정보</Text>
                                </div>
                                <Descriptions
                                    variant="outlined"
                                    column={1}
                                    size="small"
                                    className="info-descriptions"
                                >
                                    <Descriptions.Item label="닉네임">
                                        <Text strong>
                                            {playerData.playerInfo?.userInfo?.Nickname || '-'}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="계정 레벨">
                                        <Tag color="blue" className="level-tag">
                                            유저 데이터 Lv. {playerData.playerInfo?.userInfo?.Level || 0} / 서버 Lv. {playerData.lv || '-'}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="경험치">
                                        <Text strong>
                                            {playerData.playerInfo?.userInfo?.Exprience?.toLocaleString() || 0} XP
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="PID">
                                        <Text strong className="mono-text">
                                            {playerData.pid}
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        </Col>

                        {/* 계정 정보 */}
                        <Col span={24}>
                            <div className="info-section">
                                <div className="section-header">
                                    <InfoCircleOutlined className="section-icon"/>
                                    <Text strong className="section-title">계정 정보</Text>
                                </div>
                                <Descriptions
                                    variant="outlined"
                                    column={1}
                                    size="small"
                                    className="info-descriptions"
                                >
                                    <Descriptions.Item label="UserKey">
                                        <Text strong className="mono-text">
                                            {playerData.userKey}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="UID">
                                        <Text strong className="mono-text">
                                            {playerData.uid}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="UDID">
                                        <Text strong className="mono-text">
                                            {playerData.udid}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="상태">
                                        {getStateTag(playerData.userState)}
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        </Col>
                    </Row>
                </Col>

                {/* 오른쪽 영역 */}
                <Col xs={24} lg={12}>
                    <Row gutter={[0, 24]}>
                        {/* 활동 정보 */}
                        <Col span={24}>
                            <div className="info-section">
                                <div className="section-header">
                                    <ClockCircleOutlined className="section-icon"/>
                                    <Text strong className="section-title">활동 정보</Text>
                                </div>
                                <Descriptions
                                    variant="outlined"
                                    column={1}
                                    size="small"
                                    className="info-descriptions"
                                >
                                    <Descriptions.Item label="가입일">
                                        <Text strong>
                                            {convertToUTC9(playerData.created)}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="최근 로그인">
                                        <Text strong>
                                            {convertToUTC9(playerData.lastLogin)}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="최근 저장시간">
                                        <Text strong>
                                            {dayjs.unix(playerData.syncId).format('YYYY-MM-DD HH:mm:ss')}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="국가">
                                        <Tag color="geekblue" className="info-tag">
                                            {playerData.country}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="언어">
                                        <Tag color="purple" className="info-tag">
                                            {playerData.language}
                                        </Tag>
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        </Col>

                        {/* 계정 상태 관리 */}
                        <Col span={24}>
                            <div className="info-section">
                                <div className="section-header">
                                    <SettingOutlined className="section-icon"/>
                                    <Text strong className="section-title">계정 상태 관리</Text>
                                </div>

                                {isCopy ? (
                                    <div
                                        className="status-display"
                                        style={{
                                            background: 'var(--header-background)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            minHeight: '120px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Space direction="vertical" size="small">
                                            <Text type="secondary">현재 계정 상태</Text>
                                            {getStateTag(playerData.userState)}
                                            {playerData.userState === USER_STATE.SUSPEND && (
                                                <Text type="secondary" style={{fontSize: '12px'}}>
                                                    정지 해제: {dayjs.unix(playerData.stateDate).format('YYYY-MM-DD')}
                                                </Text>
                                            )}
                                            {playerData.userState === USER_STATE.WITHDRAW && (
                                                <Text type="secondary" style={{fontSize: '12px'}}>
                                                    탈퇴 일시: {dayjs.unix(playerData.stateDate).format('YYYY-MM-DD HH:mm')}
                                                </Text>
                                            )}
                                        </Space>
                                    </div>
                                ) : (
                                    <div
                                        className="status-control-section"
                                        style={{
                                            background: 'var(--header-background)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            padding: '16px',
                                            minHeight: '120px'
                                        }}
                                    >
                                        <Space direction="vertical" style={{width: '100%'}} size="middle">
                                            <div className="status-control">
                                                <Text className="status-label"
                                                      style={{display: 'block', marginBottom: 8}}>
                                                    상태 변경:
                                                </Text>
                                                <Select
                                                    value={playerData.userState || USER_STATE.DEFAULT}
                                                    onChange={(value) => handleSetPlayerState('userState', value)}
                                                    style={{width: '100%'}}
                                                    disabled={isWithdraw || playerData.userKey <= 0}
                                                    className="status-select"
                                                    size="middle"
                                                >
                                                    {userStateList.map((option) => (
                                                        <Option key={option.value} value={option.value}>
                                                            {option.value === 1 ? (playerData.stateReason === 1 ? '삭제' : '탈퇴') : option.label}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </div>

                                            {playerData.userState === USER_STATE.SUSPEND && (
                                                <div className="status-detail suspend-detail">
                                                    <Text type="secondary" className="detail-label"
                                                          style={{display: 'block', marginBottom: 8}}>
                                                        정지 해제 날짜:
                                                    </Text>
                                                    <DatePicker
                                                        value={dayjs.unix(playerData?.stateDate)}
                                                        onChange={(value) => handleSetPlayerState('stateDate', dayjs(value).endOf('day').unix())}
                                                        format='YYYY-MM-DD'
                                                        allowClear={false}
                                                        style={{width: '100%'}}
                                                        className="status-datepicker"
                                                        size="middle"
                                                    />
                                                </div>
                                            )}

                                            {playerData.userState === USER_STATE.WITHDRAW && (
                                                <Alert
                                                    message="탈퇴된 계정"
                                                    description={`탈퇴 시간: ${dayjs.unix(playerData.stateDate).format('YYYY-MM-DD HH:mm:ss')}`}
                                                    type="warning"
                                                    showIcon
                                                    size="small"
                                                />
                                            )}
                                            {playerData.userState === USER_STATE.WITHDRAW && (
                                                <Checkbox
                                                    checked={!!playerData.isDelete}
                                                    onChange={(e) => handleSetPlayerState('isDelete', e.target.checked)}
                                                    style={{marginTop: 8}}
                                                >
                                                    계정 데이터 삭제 (account_find)
                                                </Checkbox>
                                            )}

                                            {currentUser.target !== ENV.PRODUCTION && (
                                                <div className="status-control" style={{marginTop: 16}}>
                                                    <Text className="status-label"
                                                          style={{display: 'block', marginBottom: 8}}>
                                                        결제 유저로 등록:
                                                    </Text>
                                                    <Button
                                                        type="primary"
                                                        icon={<SaveOutlined/>}
                                                        onClick={handleSetPaidLogs}
                                                        disabled={!playerData.userKey}
                                                        className="action-button primary-button"
                                                        block
                                                    >
                                                        유료 결제 로그 추가
                                                    </Button>
                                                </div>
                                            )}
                                        </Space>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {!isCopy && (
                <Row gutter={[16, 24]} style={{marginTop: 24}}>
                    <Col span={24}>
                        <PlayerBadgeGrant playerData={playerData} setPlayerData={setPlayerData}/>
                    </Col>
                </Row>
            )}
        </Card>
    );
};

export default PlayerInfo;